// app/src/lib/procurement/purchaseOrders.js - PO draft/submit (US-2.11, US-2.12).
//
// Submission methods are intentionally low-tech for Phase 2: mailto: handoff to
// the operator's email client, or copy-to-clipboard for portal paste. Direct
// EDI/portal integrations are out of scope (Phase 5).

import { db } from '../db.js';
import { requireUserId, cents, isoToday } from './util.js';
import { latestFor } from './priceBook.js';
import { mutate, gate as requireWritable, record } from '../scale/instrument.js';
import * as approvals from '../scale/approvals.js';

const DEFAULT_APPROVAL_CENTS = 500_00; // $500

export async function createDraft(input) {
  const userId = await requireUserId();
  if (!input.vendorId) throw new Error('po.create: vendorId required');
  const vendor = await db.vendors.get(input.vendorId);
  if (!vendor || vendor.userId !== userId) throw new Error('po.create: vendor not found');
  if (vendor.isActive !== 1) throw new Error('po.create: vendor inactive');

  const poId = crypto.randomUUID();
  const po = {
    poId,
    userId,
    vendorId: input.vendorId,
    status: 'DRAFT',
    orderDate: isoToday(),
    notes: input.notes,
  };

  let lines = input.lines || [];
  if ((!lines || lines.length === 0) && input.copyFromLastPO) {
    const last = await mostRecentSubmittedFor(input.vendorId);
    if (last) {
      const lastLines = await db.poLineItems.where('poId').equals(last.poId).toArray();
      lines = lastLines.map((l) => ({ itemId: l.itemId, quantity: l.quantity, uom: l.uom }));
    }
  }

  const built = await Promise.all((lines || []).map(async (l) => buildLine(userId, poId, input.vendorId, l)));
  po.totalCents = built.reduce((s, l) => s + (l.extPriceCents || 0), 0);

  return mutate(
    { entityType: 'purchaseOrder', entityId: po.poId, action: 'create', after: po },
    async () => {
      await db.transaction('rw', db.purchaseOrders, db.poLineItems, async () => {
        await db.purchaseOrders.put(po);
        if (built.length) await db.poLineItems.bulkPut(built);
      });
      return { po, lines: built };
    },
  );
}

export async function addLine(poId, line) {
  const userId = await requireUserId();
  const po = await db.purchaseOrders.get(poId);
  if (!po || po.userId !== userId) throw new Error('po.addLine: not found');
  if (po.status !== 'DRAFT') throw new Error('po.addLine: not draft');
  const built = await buildLine(userId, poId, po.vendorId, line);
  await db.transaction('rw', db.purchaseOrders, db.poLineItems, async () => {
    await db.poLineItems.put(built);
    const lines = await db.poLineItems.where('poId').equals(poId).toArray();
    po.totalCents = lines.reduce((s, l) => s + (l.extPriceCents || 0), 0);
    await db.purchaseOrders.put(po);
  });
  return built;
}

export async function removeLine(poLineId) {
  const userId = await requireUserId();
  const line = await db.poLineItems.get(poLineId);
  if (!line || line.userId !== userId) return;
  const po = await db.purchaseOrders.get(line.poId);
  if (po.status !== 'DRAFT') throw new Error('po.removeLine: not draft');
  await db.transaction('rw', db.purchaseOrders, db.poLineItems, async () => {
    await db.poLineItems.delete(poLineId);
    const lines = await db.poLineItems.where('poId').equals(line.poId).toArray();
    po.totalCents = lines.reduce((s, l) => s + (l.extPriceCents || 0), 0);
    await db.purchaseOrders.put(po);
  });
}

export async function updateLineQuantity(poLineId, quantity) {
  const userId = await requireUserId();
  const line = await db.poLineItems.get(poLineId);
  if (!line || line.userId !== userId) throw new Error('po.updateLine: not found');
  const po = await db.purchaseOrders.get(line.poId);
  if (po.status !== 'DRAFT') throw new Error('po.updateLine: not draft');
  if (!Number.isFinite(quantity) || quantity < 0) throw new Error('po.updateLine: invalid quantity');
  line.quantity = quantity;
  line.extPriceCents = line.unitPriceCents != null ? Math.round(line.unitPriceCents * quantity) : undefined;
  await db.transaction('rw', db.purchaseOrders, db.poLineItems, async () => {
    await db.poLineItems.put(line);
    const lines = await db.poLineItems.where('poId').equals(line.poId).toArray();
    po.totalCents = lines.reduce((s, l) => s + (l.extPriceCents || 0), 0);
    await db.purchaseOrders.put(po);
  });
  return line;
}

export async function submit(poId, opts) {
  await requireWritable(); // read-only accounts can't mutate.
  const userId = await requireUserId();

  // Approval gate is evaluated BEFORE the status-flip transaction.
  // Calling approvals.requestApproval inside the rw transaction would open a
  // sub-transaction referencing db.approvalRequests, a table not in the outer
  // scope — Dexie rejects that. Splitting the two gives us a clean race
  // boundary: the gate decides "needs approval?"; if no, the transaction
  // flips DRAFT → SUBMITTED atomically and re-checks status to defeat races.
  const preview = await db.purchaseOrders.get(poId);
  if (!preview || preview.userId !== userId) {
    const err = new Error('po.submit: not found');
    err.code = 'NOT_FOUND';
    throw err;
  }
  if (preview.status !== 'DRAFT') {
    const err = new Error('po.submit: not draft');
    err.code = 'NOT_DRAFT';
    throw err;
  }
  const threshold = opts.approvalThresholdCents ?? DEFAULT_APPROVAL_CENTS;
  if ((preview.totalCents || 0) >= threshold && !opts.approved) {
    const req = await approvals.requestApproval('purchaseOrder', poId, preview.totalCents || 0);
    const err = new Error('approval required');
    err.code = 'APPROVAL_REQUIRED';
    err.threshold = threshold;
    err.totalCents = preview.totalCents;
    err.approvalRequestId = req.requestId;
    err.status = 'pending_approval';
    throw err;
  }

  let po, vendor, lines;
  await db.transaction('rw', db.purchaseOrders, db.poLineItems, db.vendors, async () => {
    po = await db.purchaseOrders.get(poId);
    if (!po || po.userId !== userId) {
      const err = new Error('po.submit: not found'); err.code = 'NOT_FOUND'; throw err;
    }
    if (po.status !== 'DRAFT') {
      const err = new Error('po.submit: race — no longer draft'); err.code = 'NOT_DRAFT'; throw err;
    }
    po.status = 'SUBMITTED';
    po.submittedVia = opts.via;
    po.expectedDeliveryDate = opts.expectedDeliveryDate;
    await db.purchaseOrders.put(po);
    vendor = await db.vendors.get(po.vendorId);
    lines = await db.poLineItems.where('poId').equals(poId).toArray();
  });

  // Audit AFTER the commit. Fail-soft inside `record()` so a logging failure
  // never re-throws and undoes the visible submission.
  await record({
    action: 'update',
    entityType: 'purchaseOrder',
    entityId: poId,
    before: { status: 'DRAFT' },
    after:  { status: 'SUBMITTED', submittedVia: opts.via, expectedDeliveryDate: opts.expectedDeliveryDate },
  });

  const text = formatPlainText(po, vendor, lines);
  if (opts.via === 'email') return { po, mailto: buildMailto(vendor, po, text) };
  if (opts.via === 'clipboard') return { po, clipboard: text };
  return { po };
}

export async function cancel(poId) {
  const userId = await requireUserId();
  const po = await db.purchaseOrders.get(poId);
  if (!po || po.userId !== userId) return;
  po.status = 'CANCELLED';
  await db.purchaseOrders.put(po);
}

export async function listOpen() {
  const userId = await requireUserId();
  return db.purchaseOrders
    .where('[userId+status]')
    .anyOf([[userId, 'DRAFT'], [userId, 'SUBMITTED'], [userId, 'PARTIAL']])
    .toArray();
}

async function mostRecentSubmittedFor(vendorId) {
  const rows = await db.purchaseOrders
    .where('vendorId').equals(vendorId)
    .filter((p) => p.status === 'SUBMITTED' || p.status === 'PARTIAL' || p.status === 'COMPLETE')
    .toArray();
  if (!rows.length) return null;
  rows.sort((a, b) => b.orderDate.localeCompare(a.orderDate));
  return rows[0];
}

async function buildLine(userId, poId, vendorId, l) {
  if (!l.itemId) throw new Error('po: line.itemId required');
  if (!Number.isFinite(l.quantity) || l.quantity <= 0) throw new Error('po: line.quantity must be > 0');
  // Defense-in-depth: confirm the item is owned by the same user before linking it to a PO.
  const item = await db.items.get(l.itemId);
  if (!item || item.userId !== userId) throw new Error('po: item not found');

  let unitPriceCents;
  if (Number.isFinite(l.price)) {
    unitPriceCents = cents(l.price);
  } else {
    const latest = await latestFor(vendorId, l.itemId);
    unitPriceCents = latest?.priceCents;
  }
  return {
    poLineId: crypto.randomUUID(),
    userId,
    poId,
    itemId: l.itemId,
    vendorItemId: l.vendorItemId,
    quantity: l.quantity,
    uom: l.uom || item.defaultUom || 'each',
    unitPriceCents,
    extPriceCents: unitPriceCents != null ? Math.round(unitPriceCents * l.quantity) : undefined,
    description: item.name,
  };
}

function formatPlainText(po, vendor, lines) {
  const linesText = lines.map((l) => `  ${l.quantity} ${l.uom}  ${l.description || ''}${l.unitPriceCents != null ? `  @ $${(l.unitPriceCents / 100).toFixed(2)}` : ''}`).join('\n');
  return [
    `Purchase order - ${po.orderDate}`,
    `Vendor: ${vendor?.name || ''}`,
    po.expectedDeliveryDate ? `Expected delivery: ${po.expectedDeliveryDate}` : null,
    '',
    'Items:',
    linesText,
    '',
    po.totalCents != null ? `Total: $${(po.totalCents / 100).toFixed(2)}` : null,
    '',
    po.notes ? `Notes: ${po.notes}` : null,
  ].filter(Boolean).join('\n');
}

function buildMailto(vendor, po, body) {
  const to = encodeURIComponent(vendor?.repEmail || '');
  const subject = encodeURIComponent(`Order for ${po.orderDate}`);
  return `mailto:${to}?subject=${subject}&body=${encodeURIComponent(stripControlChars(String(body)))}`;
}

/**
 * Drop ASCII control characters except TAB and LF (mailto: header-injection defense).
 * Implemented with charCodeAt to avoid embedding raw control bytes in source.
 */
function stripControlChars(s) {
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c === 9 || c === 10) { out += s[i]; continue; }
    if (c < 32 || c === 127) continue;
    out += s[i];
  }
  return out;
}
